class FacetFiltersForm extends HTMLElement {
  constructor() {
    super();
    this.onActiveFilterClick = this.onActiveFilterClick.bind(this);

    this.debouncedOnSubmit = debounce((event) => {
      this.onSubmitHandler(event);
    }, 500);

    const facetForm = this.querySelector('form');
    if (facetForm) {
      facetForm.addEventListener('input', (event) => {
        // Don't auto-submit for price range inputs
        if (event.target.closest('price-range')) return;
        this.debouncedOnSubmit(event);
      });
    }

    const facetWrapper = this.querySelector('#FacetsWrapperDesktop');
    if (facetWrapper) facetWrapper.addEventListener('keyup', onKeyUpEscape);

    // Accordion effect for filters (Desktop & Mobile)
    const accordionSelectors = '.premium-filter-bar details, .mobile-facets__main details';
    this.querySelectorAll(accordionSelectors).forEach((details) => {
      details.addEventListener('toggle', (event) => {
        if (event.target.open) {
          const parent = event.target.closest('.premium-filter-bar, .mobile-facets__main');
          if (parent) {
            parent.querySelectorAll('details').forEach((otherDetails) => {
              if (otherDetails !== event.target) {
                otherDetails.removeAttribute('open');
              }
            });
          }
        }
      });
    });

    // Auto-close dropdowns on availability & sort selection (Desktop & Mobile)
    this.addEventListener('change', (event) => {
      if (event.target.type === 'checkbox' || event.target.type === 'radio') {
        // If it's an availability or sort filter, close it
        const parentFilter = event.target.closest('[data-index*="availability"], [data-index*="sort"]');
        if (parentFilter) {
          setTimeout(() => {
            parentFilter.removeAttribute('open');
            // For mobile also close root drawer
            const rootDrawer = parentFilter.closest('details.mobile-facets__disclosure');
            if (rootDrawer) rootDrawer.removeAttribute('open');
          }, 300);
        }
      }
    });

    // Price Apply button (Desktop & Mobile)
    this.querySelectorAll('.price-apply-btn').forEach((button) => {
      button.addEventListener('click', (event) => {
        event.preventDefault();
        event.stopPropagation();
        
        // Find the parent details element before submitting
        const parentDetails = button.closest('details');
        
        this.onSubmitHandler(event);
        
        // Force close dropdown immediately
        if (parentDetails) {
          parentDetails.removeAttribute('open');
          // For mobile also close root drawer if inside one
          const rootDrawer = parentDetails.closest('details.mobile-facets__disclosure');
          if (rootDrawer) rootDrawer.removeAttribute('open');
        }
      });
    });
  }

  static setListeners() {
    const onHistoryChange = (event) => {
      const searchParams = event.state ? event.state.searchParams : FacetFiltersForm.searchParamsInitial;
      if (searchParams === FacetFiltersForm.searchParamsPrev) return;
      FacetFiltersForm.renderPage(searchParams, null, false);
    };
    window.addEventListener('popstate', onHistoryChange);
  }

  static toggleActiveFacets(disable = true) {
    document.querySelectorAll('.js-facet-remove').forEach((element) => {
      element.classList.toggle('disabled', disable);
    });
  }

  static renderPage(searchParams, event, updateURLHash = true) {
    FacetFiltersForm.searchParamsPrev = searchParams;
    const sectionId = document.getElementById('main-collection-filters').dataset.id;
    const url = `${window.location.pathname}?section_id=${sectionId}&${searchParams}`;

    // Target the grid and show loading state
    const gridContainer = document.getElementById('ProductGridContainer');
    if (gridContainer) gridContainer.classList.add('loading');

    fetch(url)
      .then((response) => response.text())
      .then((responseText) => {
        const html = responseText;
        FacetFiltersForm.renderFilters(html, event);
        FacetFiltersForm.renderProductGridContainer(html);
        FacetFiltersForm.renderProductCount(html);
        FacetFiltersForm.renderAdditionalElements(html);
      })
      .catch((e) => {
        console.error(e);
      })
      .finally(() => {
        if (gridContainer) gridContainer.classList.remove('loading');
      });

    if (updateURLHash) FacetFiltersForm.updateURLHash(searchParams);
  }

  static renderProductGridContainer(html) {
    const innerHTML = new DOMParser()
      .parseFromString(html, 'text/html')
      .getElementById('ProductGridContainer').innerHTML;

    document.getElementById('ProductGridContainer').innerHTML = innerHTML;
  }

  static renderProductCount(html) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const count = parsedHTML.getElementById('ProductCount');
    const countDesktop = parsedHTML.getElementById('ProductCountDesktop');

    if (count && document.getElementById('ProductCount')) {
      document.getElementById('ProductCount').innerHTML = count.innerHTML;
    }
    if (countDesktop && document.getElementById('ProductCountDesktop')) {
      document.getElementById('ProductCountDesktop').innerHTML = countDesktop.innerHTML;
    }
  }

  static renderFilters(html, event) {
    const parsedHTML = new DOMParser().parseFromString(html, 'text/html');
    const facetDetailsElementsFromFetch = parsedHTML.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter');
    const facetDetailsElementsFromDom = document.querySelectorAll('#FacetFiltersForm .js-filter, #FacetFiltersFormMobile .js-filter');

    // Update filters except for the one currently being interacted with (if any)
    const activeFilterId = event?.target?.closest('.js-filter')?.id;

    facetDetailsElementsFromFetch.forEach((elementToRender) => {
      if (elementToRender.id === activeFilterId) return;

      const currentElement = document.getElementById(elementToRender.id);
      if (currentElement) {
        currentElement.innerHTML = elementToRender.innerHTML;
      }
    });
  }

  static renderAdditionalElements(html) {
    const parsedHTML = typeof html === 'string' ? new DOMParser().parseFromString(html, 'text/html') : html;
    const selectors = ['.facets-reset', '.sorting', '.mobile-facets__open'];

    selectors.forEach((selector) => {
      const source = parsedHTML.querySelector(selector);
      const target = document.querySelector(selector);
      if (source && target) target.innerHTML = source.innerHTML;
    });
  }

  static updateURLHash(searchParams) {
    history.pushState({ searchParams }, '', `${window.location.pathname}${searchParams && '?'.concat(searchParams)}`);
  }

  onSubmitHandler(event) {
    event.preventDefault();
    const form = event.target.closest('form');
    const formData = new FormData(form);
    const searchParams = new URLSearchParams(formData).toString();
    this.onSubmitForm(searchParams, event);
  }

  onSubmitForm(searchParams, event) {
    FacetFiltersForm.renderPage(searchParams, event);
  }

  onActiveFilterClick(event) {
    event.preventDefault();
    const url = event.currentTarget.href.indexOf('?') == -1
      ? ''
      : event.currentTarget.href.slice(event.currentTarget.href.indexOf('?') + 1);
    FacetFiltersForm.renderPage(url);
  }
}

FacetFiltersForm.searchParamsInitial = window.location.search.slice(1);
FacetFiltersForm.searchParamsPrev = window.location.search.slice(1);
customElements.define('facet-filters-form', FacetFiltersForm);
FacetFiltersForm.setListeners();

class PriceRange extends HTMLElement {
  constructor() {
    super();
    this.querySelectorAll('input').forEach((element) => {
      element.addEventListener('change', this.onRangeChange.bind(this));
    });
  }

  onRangeChange(event) {
    const form = this.closest('facet-filters-form');
    if (form) form.onSubmitHandler(event);
  }
}

customElements.define('price-range', PriceRange);

class FacetRemove extends HTMLElement {
  constructor() {
    super();
    const facetLink = this.querySelector('a');
    if (facetLink) {
        facetLink.addEventListener('click', (event) => {
        event.preventDefault();
        const form = this.closest('facet-filters-form') || document.querySelector('facet-filters-form');
        form.onActiveFilterClick(event);
        // Close all dropdowns and mobile drawer
        document.querySelectorAll('details[open]').forEach((d) => {
          if (d.classList.contains('facets__disclosure') || d.classList.contains('js-filter') || d.classList.contains('mobile-facets__disclosure')) {
            d.removeAttribute('open');
          }
        });
        });
    }
  }
}

customElements.define('facet-remove', FacetRemove);
